import { DateRangeSelection } from '@/common/components';
import { useModal } from '@/common/components/hooks';
import useConsentedParticipantsStore from '@/modules/my-account/store/useConsentedParticipantsStore/useConsentedParticipantsStore';
import { useLayoutThreeStore } from '@modules/study-insights/store';
import { TFunction } from 'i18next';
import ParticipantLayout from '../../shared/ParticipantLayout/ParticipantLayout';
import SectionTitle from '../../shared/SectionTitle/SectionTitle';
import { PillIds } from '../../shared/types/PillIds';
import ChartsPanelWithMap from './ChartsPanelWithMap/ChartsPanelWithMap';
import { ConsentedParticipantsSubHeader } from './ConsentedParticipantsSubHeader';
import { PillId } from './LayoutThree.types';
import { LayoutThreeSidePanel } from './LayoutThreeSidePanel/LayoutThreeSidePanel';
import { CampaignTracking } from '../../CampaignTracking';
import { formatNumber } from '@/modules/study-insights/utils';
import { PartnerOrganization } from '@/modules/core/types/partnerType.type';
import TooltipByOrganization from '../../shared/TooltipByOrganization/TooltipByOrganization';

interface LayoutThreeContentProps {
  selectedPillId: PillId;
  t: TFunction;
  selectedDateRange: DateRangeSelection | undefined;
  onSelectDateRange: (selection: DateRangeSelection) => void;
}

export const LayoutThreeContent = ({
  selectedPillId,
  t,
  selectedDateRange,
  onSelectDateRange,
}: LayoutThreeContentProps): JSX.Element => {
  const { toggle, isShowing } = useModal();

  const { isLoading: isLoadingHhcParticipantData, data: hhcParticipantData } = useLayoutThreeStore();

  const { endDate: storeEndDate, startDate: storeStartDate } = useConsentedParticipantsStore();

  if (selectedPillId === PillId.CAMPAIGN_TRACKING) {
    return <CampaignTracking t={t} />;
  }

  if (selectedPillId === PillId.CONSENTED_PARTICIPANTS) {
    const consentedForDateRange = hhcParticipantData?.groupData.stats.statsData[0]?.value ?? 0;
    const consentedGoalForDateRangePercentChange = hhcParticipantData?.groupData.stats.statsData[0]?.percentChange ?? 0;

    const conversionRateForDateRange = hhcParticipantData?.groupData.stats.statsData[1]?.value ?? 0;
    const conversionRateGoalForDateRangePercentChange =
      hhcParticipantData?.groupData.stats.statsData[1]?.percentChange ?? 0;
    const totalConsentedAllTime = hhcParticipantData?.overView?.totalConsented ?? 0;
    const totalConversionRateAllTime = hhcParticipantData?.overView?.totalConversionRate ?? 0;
    const goalLine = hhcParticipantData?.overView?.goalLine ?? 0;
    const remainingConsentedGoal = hhcParticipantData?.overView?.consentedGoal ?? 0;

    return (
      <>
        <ParticipantLayout
          id="consented-layout-three"
          isLoading={isLoadingHhcParticipantData}
          sectionTitle={
            <SectionTitle
              css={{ paddingLeft: '$6' }}
              label={t('participant_recruitment.consented_participants')}
              sub={t('participant_recruitment.recruitment_data')}
            />
          }
          labelLeftTop={t('participant_recruitment.total_consented')}
          labelLeftBottom={''}
          labelRightTop={t('participant_recruitment.total_conversion_rate')}
          labelRightBottom={t('participant_recruitment.samples_received_consented')}
          parenthesisSubtitle={t('participant_recruitment.all_time')}
          numberLeft={formatNumber(totalConsentedAllTime)}
          numberRight={totalConversionRateAllTime.toString()}
          sideContent={
            <LayoutThreeSidePanel
              selectedDateRange={selectedDateRange}
              selectedSection={PillIds.CONSENTED}
              consentedForRange={consentedForDateRange}
              consentedForRangePercentChange={consentedGoalForDateRangePercentChange}
              remainingConsentedGoal={remainingConsentedGoal}
              conversionRateForRange={conversionRateForDateRange}
              conversionRateForRangePercentChange={conversionRateGoalForDateRangePercentChange}
            />
          }
          subHeader={
            <ConsentedParticipantsSubHeader
              isNavSticky={false}
              storeStartDate={storeStartDate}
              storeEndDate={storeEndDate}
              selectedDateRange={selectedDateRange}
              onDateRangeChange={onSelectDateRange}
              t={t}
              onClick={() => toggle()}
            />
          }
          tooltipPartnerEconsent={
            <TooltipByOrganization
              t={t}
              configuredPartners={[PartnerOrganization.HEALTH_PARTNERS, PartnerOrganization.HELIX_HEALTH_PARTNERS]}
              translationsKey={'participant_recruitment'}
            />
          }
        >
          <ChartsPanelWithMap
            isLoading={isLoadingHhcParticipantData}
            chartData={hhcParticipantData}
            onSelectDateRange={onSelectDateRange}
            onCloseModal={() => toggle()}
            sectionTitle={''}
            selectedSection={PillIds.CONSENTED}
            selectedDateRange={selectedDateRange}
            showMapSection={true}
            showModal={isShowing}
            goalLine={goalLine}
          />
        </ParticipantLayout>
      </>
    );
  }

  return <></>;
};
